export enum CommonKey {
    BroadcastViewership = 'broadcastViewership',
    BroadcastDemographics = 'broadcastDemographics',
    InVenueAnalytics = 'inVenueAnalytics',
    InVenueRightsholders = 'inVenueRightsholders',
    InVenueSeasons = 'inVenueSeasons',
    InVenueSections = 'inVenueSections',
    MediaValueReport = 'mediaValueReport',
    SocialPosts = 'socialPosts',
    BroadcastPosts = 'broadcastPosts',
    ClientRsps = 'clientRsps',
    Campaigns = 'campaigns',
    CampaignsInsights = 'campaignsInsights',
    AutomatedInsights = 'automatedInsights',
}

export type SearchParams = {
    [key: string]: string | boolean | number | any[];
};

export enum UserRole {
    CLIENT = 'ROLE_COORDINATOR',
    ANNOTATOR = 'ROLE_ANNOTATOR',
    MANAGER = 'ROLE_MANAGER',
    ANALYST = 'ROLE_ANALYST',
    ADMIN = 'ROLE_ADMIN',
    COORDINATOR = 'ROLE_COORDINATOR',
}

export type BasicInfo = {
    name: string;
    id: number;
    jobStatus?: string;
};

export type AutomatedParameter = {
    type: string;
    name: string;
    values: string[];
};

export type LeagueSeason = {
    id: number;
    year: number;
    leagueId: number;
    startDate: string;
    endDate: string;
};

export interface Campaign extends BasicInfo {
    jobStatus: string;
    campaignId: number;
    campaignName: string;
}

export interface Collection extends BasicInfo {
    clientId: number;
    description: string;
    automatedParameters: AutomatedParameter;
    addedAt: string | null;
    postCount: number;
    lookbackStartedAt: string | null;
    lookbackCompletedAt: string | null;
    createdBy: Manager;
    creatorName?: string;
    /** When true, collection uses League Attribution; undefined/false = Team Attribution */
    isUsingRha?: boolean;
}

export interface CampaignDetails {
    campaignName?: string;
    campaignId?: number;
    platformId?: number;
    platformName?: string;
    impressions?: number;
    organicImpressions?: number;
    paidImpressions?: number;
    videoViews?: number;
    organicVideoViews?: number;
    paidVideoViews?: number;
    engagements?: number;
    organicEngagements?: number;
    paidEngagements?: number;
    fullMediaValue?: number;
    postCount?: number;
    rowCount?: number;
    jobStatus?: string;
    addedAt?: string | Date;
}

export interface CampaignsInsights {
    total: CampaignDetails;
    values: CampaignDetails[];
}

export interface MediaValueReportItem {
    exposures: number;
    impressions: number;
    organicImpressions: number;
    paidImpressions: number;
    videoViews: number;
    organicVideoViews: number;
    paidVideoViews: number;
    engagements: number;
    organicEngagements: number;
    paidEngagements: number;
    duration: number;
    fullMediaValue: number;
    paidFullMediaValue: number;
    organicFullMediaValue: number;
    sponsorMediaValue: number;
    paidSponsorMediaValue: number;
    organicSponsorMediaValue: number;
    averageMvp: number;
    placementTypeId: number | null;
    placementTypeName: string | null;
    rightsholderPlacementId: any;
    rightsholderPlacementName: string | null;
    rightsholderId: number | null;
    rightsholderName: string | null;
    sponsorId: number | null;
    sponsorName: string | null;
    accountOwnershipTypeId: number | null;
    accountOwnershipTypeName: string | null;
    socialPlatformId: number | null;
    socialPlatformName: string | null;
    mediaTypeId: number | null;
    mediaTypeName: string | null;
    date: string | null;
    collectionId: number | null;
    collectionName: string | null;
    postCount: number;
    accountUsername: string | null;
    socialAccountGroupName: string | null;
    socialAccountGroupId: number | null;
    mediaUploadName: string | null;
    gameName: string | null;
    broadcastCount: number;
    inVenueGameCount: number;
    exposuresImpressions: number;
    countryId: number | null;
    countryName: string | null;
    postType: string | null;
}

export interface MediaValueReport {
    total: MediaValueReportItem;
    values: MediaValueReportItem[];
}

export interface RightsholderLogo {
    logoId: number;
    url: string;
    main: boolean;
}

export interface RightsholderPlacement {
    id: number;
    name: string;
    placementType: PlacementType;
    active: boolean;
    virtual: boolean | null;
    examples: any[];
    eligibleGameTypes: any[];
    cvAnalysisEnabled: boolean;
    clientRemoved: boolean;
    hasCrspValuationRestrictions: boolean;
    pendingRspFramesWithBoundingBoxes: boolean;
    placementTypeExampleImages: any[];
}

export interface PlacementType {
    id: number;
    name: string;
}

export interface Manager {
    id: number;
    username: string;
    email: any;
    firstName: any;
    lastName: any;
    csvDelimiter: any;
    active: any;
    roles: any[];
    clients: any[];
    agreementsAccepted: any[];
    gumgumUser: boolean;
    internalUser: boolean;
    emailSubscriptionKey: any;
    subscribedEmailTypeIds: any[];
}

export interface Currency extends BasicInfo {
    id: number;
    name: string;
    code: string;
}

export interface ClientPermission extends BasicInfo {
    id: number;
    description: string;
    name: string;
}

export interface Client {
    id: number;
    name: string;
    active: boolean;
    currency: Currency | null;
    permissions: number[];
    headerImageUrl: string | null;
    valuationStart: Date | null;
    valuationEnd: Date | null;
    primaryColor: string | null;
    primaryTextColor: string | null;
    secondaryColor: string | null;
    secondaryTextColor: string | null;
    socialQaAnalyst?: User | null;
    platformIds: number[];
    qaPendingPostsCount?: number;
    valuationStatusId: number | string;
    configStatusId: number | string;
    // Persona type (Team/Brand/Agency) for the selected client. Arrives via the /users payload
    // (SP-8879); optional because the API omits it when unset. For future use — not yet rendered.
    personaTypeId?: number | null;
    users: User[] | null;
}

export interface User {
    id: number;
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    active: boolean;
    roles: string[];
    clients?: Client[];
    clientsById?: Record<number, Client>;
    agreementsAccepted: boolean;
    internalUser: boolean;
    emailSubscriptionKey: string | null;
    subscribedEmailTypeIds: [] | null;
    csvDelimiter: string;
    gumgumUser: boolean;
    xat: string;
    timestamp: number;
    usersList: User[];
    isLoggedIn?: boolean;
    selectedClientId?: string;
    token?: string;
    userEmail?: string;
    // front end props
    isAuthenticated?: boolean;
    isSelectingClient?: boolean;
    isRequestingLink?: boolean;
    isLoading?: boolean;
    error?: {
        message?: string;
        ssoError?: boolean;
    };
    userPreferences: {
        rightsholder_attribution: {
            [clientId: number]: string;
        };
    };
}

export interface Rightsholder {
    id: number;
    name: string;
    logoUrl: string;
    manager: Manager;
    active: boolean;
    rightsholderLogos: RightsholderLogo[];
    sponsors: any[];
    rightsholderPlacements: RightsholderPlacement[];
    mainLogo: RightsholderLogo;
    annotationPriority: number;
    leagueId: number;
    leagueName: string;
    sportId: number;
    gameTypeId: number;
    streams: any[];
    accounts: any[];
    metadata: any[];
    validPeriodsForAnnotation: any[];
}

export interface BroadcastNetworkViewership {
    broadcastName: string;
    averageViewership: number;
    individualViewership: number;
    contentDate: string;
    smv: number;
    networkName: string;
    exposuresImpressions: number;
    countryName: string;
}

export interface BroadcastViewership {
    broadcastId: number;
    name: string;
    averageViewership: number;
    individualViewership: number;
    contentDate: string;
    countryName: string;
    networkViewerships: BroadcastNetworkViewership[];
}
export interface Broadcast {
    broadcastName: string;
    contentDate: string;
    broadcastRsps: BroadcastRsp[];
}

export interface BroadcastRsp {
    rightsholder: Rightsholder;
    sponsor: Sponsor;
    placement: RightsholderPlacement;
    networks: string[];
    averageMvpScore: number;
    duration: number;
    exposures: number;
    sponsorMediaValue: number;
    frameUrl: string;
    impressions: number;
    // New fields for VM exposures
    totalVerbalMentions?: number;
    verbalMentionsExamples?: {
        rightsholder: Rightsholder;
        sponsor: Sponsor;
        placement: RightsholderPlacement;
        networks: Network[];
        broadcastName: string;
        averageMvpScore: number;
        sponsorMediaValue: number;
        impressions: number;
        startAt: number;
        endAt: number;
        duration: number;
        mention?: string;
        context?: string;
        type?: string;
        videoUrl?: string;
        frameUrl?: string;
    }[];
    startAt?: number;
    endAt?: number;
    mention?: string;
    context?: string;
    type?: string;
    videoUrl?: string;
}

export interface BroadcastFrameBox {
    topLeftX: number;
    topLeftY: number;
    width: number;
    height: number;
}

export interface BroadcastFrameExample {
    frameId: number;
    frameUrl: string;
    /** Offset of the frame within the broadcast, in milliseconds. */
    capturedAt: number | null;
    /** Exposure window this frame was selected for, in seconds. */
    exposureStartAt: number | null;
    exposureEndAt: number | null;
    /** Total broadcast duration in seconds. */
    broadcastDuration: number | null;
    /** Source pixel dimensions the box coordinates are expressed in. */
    frameWidth: number | null;
    frameHeight: number | null;
    boxes: BroadcastFrameBox[];
}

export interface Network {
    id: number;
    platformUserId: number | null;
    platformId: number | null;
    username: string | null;
    name: string;
    profileUrl: string | null;
    profilePictureUrl: string | null;
    followersCount: number | null;
    addedAt: string | null;
    countryId?: number | null;
    countryName?: string | null;
}

export interface VerbalMentionRsp {
    rightsholder: Rightsholder;
    sponsor: Sponsor;
    placement: RightsholderPlacement;
    network: Network;
    broadcastName: string;
    averageMvpScore: number;
    sponsorMediaValue: number;
    impressions: number;
    startAt: number;
    endAt: number;
    duration: number;
    mention: string;
    context: string;
    type: string;
    videoUrl: string;
    audioUrl: string;
    frameUrl: string | null;
    mentionId: number;
}

export interface VerbalMentions {
    broadcastName: string;
    contentDate: string;
    broadcastRsps?: VerbalMentionRsp[];
    totalByBroadcast: number;
}

export interface VerbalExposuresResponse {
    verbalExposures?: VerbalMentions[];
    total?: number;
}

export interface Sponsor {
    id: number;
    name: string;
}

export interface ReportResponse {
    isProcessing?: boolean;
    errorType?: string;
    message?: string;
    url?: string;
    name?: string;
    exportAs?: string;
}
